import { useAuth0 } from '@auth0/auth0-react';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useMemo } from 'react';
import { Role, SaviWebAuth0User, userRolesIndex } from 'utils/helpers/auth0.helpers';

type User = {
  isLoading: boolean;
};

// an unauthorized user
export type UnauthorizedUser = User & {
  isAuthenticated: false;
};

// a user is has an auth0 token and credentials
export type AuthorizedUser = User &
  SaviWebAuth0User & {
    isAuthenticated: true;
  };

export type Auth0UserState = AuthorizedUser | UnauthorizedUser;

export const useAuth0User = (): Auth0UserState => {
  const { isAuthenticated, isLoading, user: auth0User } = useAuth0();
  const { data: currentUser } = useCurrentUser();

  return useMemo(() => {
    const auth0UserTyped = auth0User as SaviWebAuth0User;

    // AuthorizedUser
    if (isAuthenticated) {
      // some SSO connections (e.g. Uniting Care) don't issue a role claim; so a roleless
      // token gets currentUser?.role or defaults to Hospital User
      const roles = auth0UserTyped?.[userRolesIndex]?.length
        ? auth0UserTyped[userRolesIndex]
        : [currentUser?.role || Role.SaviConnectUser];

      return { isAuthenticated: true, ...auth0UserTyped, [userRolesIndex]: roles, isLoading };
    }

    // UnauthorizedUser
    return { isAuthenticated: false, isLoading };
  }, [auth0User, currentUser?.role, isAuthenticated, isLoading]);
};
